import { compareDesc } from "date-fns";

import { SectionRole } from "../../shared/types/roles";

import { createAppSelector, sectionUserSelectors } from "./base";
import {
  selectCurrentBreakoutGroupId,
  selectCurrentListenedBreakoutGroupId,
  selectIsInBreakout,
} from "./breakouts";
import { selectCurrentClassUser, selectCurrentClassUserId } from "./class-user";
import { selectMaxAvActiveParticipants } from "./org";
import { selectIsRecordingAgent, selectUserIdsWhoCanAuthorSection } from "./permissions";
import {
  selectAvActiveClassUserIds,
  selectClassStateLastUpdatedAt,
  selectClassUserIdsPresentInClass,
  selectMaxParticipantBarSlots,
  selectParticipantBarVisibleClassUserIds,
  selectPinnedClassUserIds,
  selectRecentSpeakerClassUserIds,
} from "./pubsub-virtual-class-state";
import {
  selectClassUsersWithIsVisibleInClassCapability,
  selectCurrentUserHasVisibleInClassCapability,
  selectScreensharingClassUserId,
} from "./virtual-classes";

import { ClassUserType, SectionUserType } from "components/server-types";
import { isAvLimitActiveParticipantsEnabled } from "utils/waffle-flags";

const SECTION_ROLE_ORDERING: SectionRole[] = [
  SectionRole.INSTRUCTOR,
  SectionRole.SECTION_ASSISTANT,
  SectionRole.STUDENT,
];

export type SortVirtualClassUsersContext = {
  sectionUsers: SectionUserType[];
  screenshareClassUserId?: string | null;
  recentSpeakerIds?: string[];
  currentClassUserId?: string | null;
  roleOnly?: boolean;
  roleAndUserIdOnly?: boolean;
};

export function canonicalSortVirtualClassUsers(
  users: ClassUserType[],
  ctx: SortVirtualClassUsersContext,
): ClassUserType[] {
  return users.toSorted((a, b) => {
    const roleMap = new Map<string, SectionRole>(
      ctx.sectionUsers.map((sectionUser) => [sectionUser.user_id, sectionUser.role]),
    );

    if (ctx.currentClassUserId) {
      if (a.id === ctx.currentClassUserId) return 1;
      if (b.id === ctx.currentClassUserId) return -1;
    }

    const roleA = roleMap.get(a.user_id) || SectionRole.STUDENT;
    const roleB = roleMap.get(b.user_id) || SectionRole.STUDENT;
    if (roleA !== roleB) {
      const aIdx = SECTION_ROLE_ORDERING.indexOf(roleA);
      const bIdx = SECTION_ROLE_ORDERING.indexOf(roleB);
      if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
      return aIdx >= 0 ? -1 : 1;
    }

    if (ctx.roleOnly) return 0;
    if (ctx.roleAndUserIdOnly) return a.user_id.localeCompare(b.user_id);
    if (a.hand_raised_at && b.hand_raised_at) {
      return compareDesc(b.hand_raised_at, a.hand_raised_at);
    }

    if (a.hand_raised_at) return -1;
    if (b.hand_raised_at) return 1;

    if (ctx.screenshareClassUserId) {
      if (a.id === ctx.screenshareClassUserId) return -1;
      if (b.id === ctx.screenshareClassUserId) return 1;
    }

    if (ctx.recentSpeakerIds?.length) {
      const aOrder = ctx.recentSpeakerIds.indexOf(a.id);
      const bOrder = ctx.recentSpeakerIds.indexOf(b.id);
      const aVal = aOrder === -1 ? Number.MAX_SAFE_INTEGER : aOrder;
      const bVal = bOrder === -1 ? Number.MAX_SAFE_INTEGER : bOrder;
      if (aVal !== bVal) return aVal - bVal;
    }

    const aSort = a.av_sort_order;
    const bSort = b.av_sort_order;
    if (aSort !== null && bSort !== null) return aSort - bSort;
    if (aSort !== null) return -1;
    if (bSort !== null) return 1;

    return a.user_id.localeCompare(b.user_id);
  });
}

// Source: visible by role + present. Main-call and filtered (breakout) selectors derive from this.
const selectPresentClassUsersWithIsVisibleInClassCapability = createAppSelector(
  [selectClassUsersWithIsVisibleInClassCapability, selectClassUserIdsPresentInClass],
  (classUsersVisibleByRole, presentClassUserIds) =>
    classUsersVisibleByRole.filter((cu) => presentClassUserIds.includes(cu.id)),
);

// Gets users that are in the same room as the current user (breakout or main call)
export const selectPresentClassUsersFilteredByRoom = createAppSelector(
  [
    selectPresentClassUsersWithIsVisibleInClassCapability,
    selectIsInBreakout,
    selectIsRecordingAgent,
    selectCurrentBreakoutGroupId,
  ],
  (visibleAndPresentClassUsers, isInBreakout, isRecordingAgent, currentBreakoutId) =>
    visibleAndPresentClassUsers.filter((classUser) => {
      if (!isInBreakout) return true;
      // Recording agents should not see anyone in a breakout
      if (isRecordingAgent) return false;
      if (currentBreakoutId) {
        return classUser.breakout_group_id === currentBreakoutId;
      }
      return classUser.breakout_group_id === null;
    }),
);
// Locally calculated participant bar leader from presence + roles.
// Authoritative leader for publishing is leader_class_user_id in pubsub virtual class state.
export const selectCalculatedParticipantBarLeaderClassUser = createAppSelector(
  [
    selectPresentClassUsersWithIsVisibleInClassCapability,
    sectionUserSelectors.filterForCurrentSection,
    selectUserIdsWhoCanAuthorSection,
    selectClassStateLastUpdatedAt,
  ],
  (
    classUsersInVirtualClassPresent,
    sectionUsers,
    userIdsWhoCanAuthorSection,
    classStateLastUpdatedAt,
  ) => {
    if (classUsersInVirtualClassPresent.length === 0) return null;
    if (classStateLastUpdatedAt === null) return null;
    const orderedClassUsers = canonicalSortVirtualClassUsers(classUsersInVirtualClassPresent, {
      sectionUsers,
      roleAndUserIdOnly: true,
    });
    const firstClassUser = orderedClassUsers[0];
    if (firstClassUser && userIdsWhoCanAuthorSection.includes(firstClassUser.user_id)) {
      return firstClassUser;
    }
    return null;
  },
);

export const selectClassHasCalculatedParticipantBarLeader = createAppSelector(
  [selectCalculatedParticipantBarLeaderClassUser],
  (calculatedParticipantBarLeaderClassUser) => !!calculatedParticipantBarLeaderClassUser,
);

export const selectIsCalculatedParticipantBarLeader = createAppSelector(
  [selectCurrentClassUserId, selectCalculatedParticipantBarLeaderClassUser],
  (currentClassUserId, calculatedParticipantBarLeaderClassUser) => {
    return calculatedParticipantBarLeaderClassUser?.id === currentClassUserId;
  },
);

export const selectClassUsersInListenedBreakoutGroup = createAppSelector(
  [selectPresentClassUsersWithIsVisibleInClassCapability, selectCurrentListenedBreakoutGroupId],
  (classUsersVisibleByRoleAndPresent, listenedBreakoutGroupId) => {
    if (!listenedBreakoutGroupId) {
      return [];
    }
    return classUsersVisibleByRoleAndPresent.filter(
      (classUser) => classUser.breakout_group_id === listenedBreakoutGroupId,
    );
  },
);

export const selectAvActiveClassUsers = createAppSelector(
  [selectAvActiveClassUserIds, selectPresentClassUsersWithIsVisibleInClassCapability],
  (avActiveClassUserIds, classUsersVisibleByRoleAndPresent) => {
    return classUsersVisibleByRoleAndPresent.filter((classUser) =>
      avActiveClassUserIds.includes(classUser.id),
    );
  },
);

// We consider a class large if the limit is active and the current present users are more than the max av active participants
export const selectIsLargeClass = createAppSelector(
  [selectPresentClassUsersWithIsVisibleInClassCapability, selectMaxAvActiveParticipants],
  (classUsersVisibleByRoleAndPresent, maxAvActiveParticipants) =>
    isAvLimitActiveParticipantsEnabled() &&
    classUsersVisibleByRoleAndPresent.length > maxAvActiveParticipants,
);
// Calculated list for the active participants. Leader always computes this for all users and distributes it.
// Order: role, hand raised, screenshare first; then recent speakers (by recency); then av_sort_order, section_user_id.
export const selectCalculatedListForActiveParticipants = createAppSelector(
  [
    selectPresentClassUsersWithIsVisibleInClassCapability,
    sectionUserSelectors.filterForCurrentSection,
    selectMaxAvActiveParticipants,
    selectScreensharingClassUserId,
    selectRecentSpeakerClassUserIds,
    selectPinnedClassUserIds,
  ],
  (
    classUsersVisibleByRoleAndPresent,
    sectionUsers,
    maxAvActiveParticipants,
    screenshareClassUserId,
    recentSpeakerIds,
    pinnedClassUserIds,
  ) => {
    const ordered = canonicalSortVirtualClassUsers(classUsersVisibleByRoleAndPresent, {
      sectionUsers,
      screenshareClassUserId: screenshareClassUserId ?? null,
      recentSpeakerIds,
    });

    const pinnedParticipants = pinnedClassUserIds
      .map((id) => ordered.find((u) => u.id === id))
      .filter((u): u is ClassUserType => u != null);
    const pinnedIdSet = new Set(pinnedClassUserIds);
    const remainingParticipants = ordered.filter((u) => !pinnedIdSet.has(u.id));
    const listWithPinnedParticipants = [...pinnedParticipants, ...remainingParticipants];

    // We order the list in a consistent way to avoid flickering.
    // The display order will be calculated locally in each client.
    const list = isAvLimitActiveParticipantsEnabled()
      ? listWithPinnedParticipants.slice(0, maxAvActiveParticipants)
      : listWithPinnedParticipants;
    return list.map((u) => u.id).sort((a, b) => a.localeCompare(b));
  },
);

/** Sizes along the participant-bar / leader pipeline (for debug_virtual_class logging). */
export const selectParticipantBarPipelineCounts = createAppSelector(
  [
    selectClassUserIdsPresentInClass,
    selectClassUsersWithIsVisibleInClassCapability,
    selectPresentClassUsersWithIsVisibleInClassCapability,
    selectCalculatedListForActiveParticipants,
  ],
  (presentClassUserIds, visibleInClass, presentAndVisible, calculatedIds) => ({
    virtualClassStatePresentClassUserIdCount: presentClassUserIds.length,
    visibleInVirtualClassCount: visibleInClass.length,
    presentAndVisibleInClassCount: presentAndVisible.length,
    calculatedListForActiveParticipantsCount: calculatedIds.length,
  }),
);

// In breakout: use local list (current breakout group)
// If the class has a participant bar leader and AV_LIMIT_ACTIVE_PARTICIPANTS is on, use the distributed AvActive list for the bar.
// Otherwise use the local list (leader may still publish av_active via WS for when the flag is enabled elsewhere).

export type ParticipantBarInputSource =
  | "distributed"
  | "local-no-leader"
  | "local-breakouts"
  | "local-no-leader-limited";

export const selectParticipantBarInputSource = createAppSelector(
  [
    selectIsInBreakout,
    selectAvActiveClassUsers,
    selectClassHasCalculatedParticipantBarLeader,
    selectPresentClassUsersFilteredByRoom,
    selectMaxAvActiveParticipants,
  ],
  (
    isInBreakout,
    avActiveClassUsers,
    classHasCalculatedParticipantBarLeader,
    presentClassUsersFilteredByRoom,
    maxAvActiveParticipants,
  ) => {
    const useDistributed =
      classHasCalculatedParticipantBarLeader &&
      !isInBreakout &&
      isAvLimitActiveParticipantsEnabled();
    if (useDistributed) {
      return { source: "distributed", classUsers: avActiveClassUsers };
    }
    if (isInBreakout) {
      return { source: "local-breakouts", classUsers: presentClassUsersFilteredByRoom };
    }
    if (!isAvLimitActiveParticipantsEnabled()) {
      return { source: "local-no-leader", classUsers: presentClassUsersFilteredByRoom };
    }
    return {
      source: "local-no-leader-limited",
      classUsers: presentClassUsersFilteredByRoom.slice(0, maxAvActiveParticipants),
    };
  },
);

export const selectClassUsersFromParticipantBarInputSource = createAppSelector(
  [selectParticipantBarInputSource],
  (input) => input.classUsers,
);

// This selector sorts the class users from the participant bar input source to adapt to the available visible slots.
// The order of priorities are:
// 1. Local user
// 2. Featured participants
// 3. Recent speakers
// 4. Instructors
// 5. Raised hands
// 6. Screenshare
// 7. AV sort order
// Within the subset of visible users, we ensure instructors, if present, are always first and the local user is always last.
// The previous visible ids are used to maintain stability of the visible list when possible.
export const selectClassUsersFromParticipantBarInputSorted = createAppSelector(
  [
    selectClassUsersFromParticipantBarInputSource,
    sectionUserSelectors.filterForCurrentSection,
    selectScreensharingClassUserId,
    selectRecentSpeakerClassUserIds,
    selectMaxParticipantBarSlots,
    selectCurrentClassUserId,
    selectParticipantBarVisibleClassUserIds,
    selectPinnedClassUserIds,
  ],
  (
    classUsersForParticipantBarInput,
    sectionUsers,
    screenshareClassUserId,
    recentSpeakers,
    maxSlots,
    currentClassUserId,
    currentVisibleIds,
    pinnedClassUserIds,
  ) => {
    const ordered = canonicalSortVirtualClassUsers(classUsersForParticipantBarInput, {
      sectionUsers,
      screenshareClassUserId: screenshareClassUserId ?? null,
      currentClassUserId: currentClassUserId ?? null,
    });

    let currentUser: ClassUserType | undefined;
    if (currentClassUserId) {
      const idx = ordered.findIndex((u) => u.id === currentClassUserId);
      if (idx >= 0) {
        // Remove current user from ordered to handle them separately later
        [currentUser] = ordered.splice(idx, 1);
      }
    }

    const maxSlotsAdjustedWithLocal = maxSlots - (currentUser ? 1 : 0);

    // Start by prioritizing pinned participants.
    let visible: ClassUserType[] = pinnedClassUserIds
      .map((id) => ordered.find((u) => u.id === id))
      .filter((u): u is ClassUserType => u != null)
      .slice(0, maxSlotsAdjustedWithLocal);

    // Then prioritize recent speakers. We keep the recentSpeakers order as is
    // since it is already by recency and avoid duplicating users.
    visible = visible
      .concat(
        recentSpeakers
          .map((id) => ordered.find((u) => u.id === id))
          // recent speakers may include users not in ordered
          .filter((u): u is ClassUserType => u != null && !visible.some((v) => v.id === u.id)),
      )
      .slice(0, maxSlotsAdjustedWithLocal);

    // When possible, fill up the list with users from the original order, skipping those already included
    for (const user of ordered) {
      if (visible.length >= maxSlotsAdjustedWithLocal) break;
      if (!visible.some((u) => u.id === user.id)) {
        visible.push(user);
      }
    }
    visible = ordered.filter((u) => visible.some((v) => v.id === u.id));

    // Use the current visible ids to maintain stability of the visible list when possible.
    if (currentVisibleIds && currentVisibleIds.length > 0) {
      const currentVisiblePositionMap = new Map(currentVisibleIds.map((id, index) => [id, index]));
      let stableVisible = [...visible];
      stableVisible.sort((a, b) => {
        const posA = currentVisiblePositionMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
        const posB = currentVisiblePositionMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;

        // If both users were previously visible, maintain their relative positions
        if (currentVisibleIds.includes(a.id) && currentVisibleIds.includes(b.id)) {
          return posA - posB;
        }

        // If only one was previously visible, that one should stay in its relative position
        // This ensures we don't disrupt existing stable ordering
        if (currentVisibleIds.includes(a.id)) return -1;
        if (currentVisibleIds.includes(b.id)) return 1;

        // For users not previously visible, maintain canonical priority
        return 0;
      });

      // Regardless of current visible ids, we sort by role to ensure instructors are always first
      stableVisible = canonicalSortVirtualClassUsers(stableVisible, {
        sectionUsers,
        roleOnly: true,
      });

      visible = stableVisible;
    }
    if (currentUser) {
      // The currentUser will always be last
      visible.push(currentUser);
    }
    // We build the final list including the hidden but present participants.
    // These are all needed by the ParticipantBar component to render all participants.
    const visibleIds = new Set(visible.map((u) => u.id));
    const hidden = ordered.filter((u) => !visibleIds.has(u.id));
    return [...visible, ...hidden];
  },
);

// Takes the sorted list of class users and injects the current user if they are visible and not already in the list.
// We do this to allow the local user to always see their own video in the participant bar.
export const selectClassUsersForParticipantBar = createAppSelector(
  [
    selectClassUsersFromParticipantBarInputSorted,
    selectMaxParticipantBarSlots,
    selectCurrentClassUser,
    selectCurrentUserHasVisibleInClassCapability,
  ],
  (classUsersFromInputSorted, maxSlots, currentClassUser, currentUserIsVisible) => {
    const shouldInject =
      currentUserIsVisible &&
      !classUsersFromInputSorted.some((classUser) => classUser.id === currentClassUser?.id);
    if (!shouldInject || !currentClassUser) {
      return classUsersFromInputSorted;
    }
    if (maxSlots > classUsersFromInputSorted.length) {
      return [...classUsersFromInputSorted, currentClassUser];
    }
    const visible = classUsersFromInputSorted.slice(0, maxSlots);
    const hidden = classUsersFromInputSorted.slice(maxSlots);
    const displaced = visible[visible.length - 1];
    if (!displaced) {
      return classUsersFromInputSorted;
    }
    const newVisible = visible.slice(0, -1).concat(currentClassUser);
    const newHidden = [displaced, ...hidden];
    return [...newVisible, ...newHidden];
  },
);

// Returns class user ids who are hidden due to the size of the participant bar,
// not because they are in a different room
export const selectClassUserIdsHiddenInParticipantBar = createAppSelector(
  [selectClassUsersForParticipantBar, selectMaxParticipantBarSlots],
  (sortedClassUsers, visibleCount) =>
    sortedClassUsers.slice(visibleCount).map((classUser) => classUser.id),
);

// This means the user is being subscribed to by the rest of the participants in the room.
export const selectIsCurrentUserInDistributedParticipantBar = createAppSelector(
  [selectClassUsersFromParticipantBarInputSorted, selectCurrentClassUserId],
  (classUsersFromParticipantBarInputSorted, currentClassUserId) =>
    classUsersFromParticipantBarInputSorted.some(
      (classUser) => classUser.id === currentClassUserId,
    ),
);
